// Recorrido del presupuesto (Martin, 8 sep 2026): la línea de seis pasos que
// le dice al vendedor dónde está la venta y qué toca hacer.
//   creación → versión → envío al cliente → respuesta → aprobación → nota de venta
// La regla vive en el backend (PresupuestoRecorridoService): el detalle recibe
// `recorrido.pasos` con estado/detalle por nodo y acá SOLO se pinta. El listado
// usa <RecorridoMini nodos={...} /> con el resumen por fila.
import { Link } from 'react-router-dom'
import { colors, text, sx, radius } from '../design/tokens'

const ORDEN = ['creacion', 'version', 'envio', 'respuesta', 'nota_venta']

// Aspecto de cada estado de nodo. Color solo donde hay estado: hecho/actual en
// el azul primario, rechazo en rojo, alerta (vencido / espera aprobación) en ámbar.
const NODO = {
  hecho: { bg: colors.primary, border: colors.primary, color: colors.bg, halo: null },
  actual: { bg: colors.bg, border: colors.primary, color: colors.primary, halo: colors.linkBg },
  pendiente: { bg: colors.bg, border: colors.textPlh, color: colors.textMeta, halo: null },
  rechazo: { bg: colors.err, border: colors.err, color: colors.bg, halo: null },
  alerta: { bg: colors.bg, border: colors.warnStrong, color: colors.warnStrong, halo: colors.warnBg },
}

const simbolo = (paso, i) => (paso.estado === 'hecho' ? '✓' : paso.estado === 'rechazo' ? '✕' : String(i + 1))

export default function RecorridoPresupuesto({ recorrido }) {
  if (!recorrido?.pasos?.length) return null
  const pasos = recorrido.pasos

  return (
    <div style={{ margin: '4px 0 6px' }}>
      <div style={{ display: 'grid', gridTemplateColumns: `repeat(${pasos.length}, 1fr)`, position: 'relative' }}>
        {pasos.map((paso, i) => {
          const n = NODO[paso.estado] || NODO.pendiente
          // La línea entre nodos se pinta azul hasta el último nodo alcanzado
          const alcanzado = paso.estado !== 'pendiente'
          const siguienteAlcanzado = pasos[i + 1] && pasos[i + 1].estado !== 'pendiente'
          const detalleColor = paso.estado === 'actual' ? colors.link : paso.estado === 'rechazo' ? colors.err : paso.estado === 'alerta' ? colors.warn : colors.textSec
          return (
            <div key={paso.clave} style={{ position: 'relative', textAlign: 'center', padding: '0 6px' }}>
              {i > 0 && <div style={{ position: 'absolute', top: 14, left: 0, right: '50%', height: 2, background: alcanzado ? colors.primary : colors.border }} />}
              {i < pasos.length - 1 && <div style={{ position: 'absolute', top: 14, left: '50%', right: 0, height: 2, background: alcanzado && siguienteAlcanzado ? colors.primary : colors.border }} />}
              <div style={{
                position: 'relative', zIndex: 1, width: 30, height: 30, borderRadius: '50%', margin: '0 auto 8px',
                display: 'grid', placeItems: 'center', fontSize: text.body, fontWeight: 700,
                background: n.bg, border: `2px solid ${n.border}`, color: n.color,
                boxShadow: n.halo ? `0 0 0 4px ${n.halo}` : 'none',
              }}>{simbolo(paso, i)}</div>
              <div style={{ fontSize: text.sec, fontWeight: paso.estado === 'pendiente' ? 500 : 600, color: paso.estado === 'pendiente' ? colors.textMeta : colors.text }}>{paso.label}</div>
              <div style={{ fontSize: text.meta, color: detalleColor, marginTop: 2, fontWeight: paso.estado === 'actual' ? 600 : 400, minHeight: 14 }}>
                {paso.clave === 'nota_venta' && paso.nota_venta_id
                  ? <Link to={`/notas-venta/${paso.nota_venta_id}`} style={{ ...sx.mono, color: colors.link, fontWeight: 600 }}>{paso.numero_lgr}</Link>
                  : (paso.detalle || '—')}
              </div>
            </div>
          )
        })}
      </div>
      {recorrido.bucle && (
        <div style={{ textAlign: 'center', marginTop: 6, fontSize: text.sec, color: colors.err }}>
          ↩ El rechazo vuelve a Versión: se corrige y se reenvía bajo el mismo número
        </div>
      )}
    </div>
  )
}

/** Versión compacta para el listado: seis puntos unidos, sin texto. */
export function RecorridoMini({ nodos, title }) {
  if (!nodos) return null
  return (
    <span style={{ display: 'inline-flex', alignItems: 'center' }} title={title}>
      {ORDEN.map((clave, i) => {
        const estado = nodos[clave] || 'pendiente'
        const n = NODO[estado] || NODO.pendiente
        const alcanzado = estado !== 'pendiente'
        return (
          <span key={clave} style={{ display: 'inline-flex', alignItems: 'center' }}>
            {i > 0 && <span style={{ display: 'inline-block', width: 8, height: 1.5, background: alcanzado ? colors.primary : colors.border }} />}
            <span style={{
              display: 'inline-block', width: 9, height: 9, borderRadius: '50%',
              background: estado === 'actual' || estado === 'alerta' ? n.halo : n.bg,
              border: `1.5px solid ${n.border}`,
            }} />
          </span>
        )
      })}
    </span>
  )
}

/**
 * Chip de versión (v1, v2…): UNA sola geometría para la cabecera del detalle y
 * el listado. `activa` = la versión que se está viendo / la vigente.
 */
export function ChipVersion({ version, activa = false, title, onClick }) {
  const style = {
    ...sx.mono, display: 'inline-block', fontSize: text.meta, fontWeight: 600, padding: '2px 7px', borderRadius: radius.xs,
    background: activa ? colors.linkBg : colors.bgSoft, color: activa ? colors.link : colors.textSec,
    border: `1px solid ${activa ? colors.linkBorder : colors.border}`, lineHeight: 1.4,
  }
  if (onClick) {
    return <button type="button" onClick={onClick} title={title} style={{ ...style, cursor: 'pointer' }}>v{version}</button>
  }
  return <span title={title} style={style}>v{version}</span>
}
